export interface DocumentOntologyContractViolation {
  code: string;
  focusNode: string;
  message: string;
}

interface TermLike {
  value: string;
}

interface QuadLike {
  subject: TermLike;
  predicate: TermLike;
  object: TermLike;
}

type DatasetLike = Iterable<QuadLike>;

const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const CDOC = "https://cogo.xyz/ns/v1/document-application#";

class GraphView {
  private readonly quads: QuadLike[];

  constructor(dataset: DatasetLike) {
    this.quads = [...dataset];
  }

  objects(subject: string | undefined, predicate: string): string[] {
    if (!subject) return [];
    return this.quads
      .filter(
        (quad) =>
          quad.subject.value === subject && quad.predicate.value === predicate,
      )
      .map((quad) => quad.object.value);
  }

  object(subject: string | undefined, predicate: string): string | undefined {
    return this.objects(subject, predicate)[0];
  }

  subjects(predicate: string, object: string): string[] {
    return this.quads
      .filter(
        (quad) =>
          quad.predicate.value === predicate && quad.object.value === object,
      )
      .map((quad) => quad.subject.value);
  }

  instances(...classNames: string[]): string[] {
    return [
      ...new Set(
        classNames.flatMap((className) =>
          this.subjects(RDF_TYPE, `${CDOC}${className}`),
        ),
      ),
    ];
  }

  has(subject: string | undefined, predicate: string, object: string): boolean {
    return this.objects(subject, predicate).includes(object);
  }

  hasType(subject: string | undefined, className: string): boolean {
    return this.has(subject, RDF_TYPE, `${CDOC}${className}`);
  }
}

const p = (localName: string) => `${CDOC}${localName}`;

interface TargetFeatureContract {
  featureKind: string;
  capabilityStatus: "supported" | "unsupported";
  implementationResponsibility: "actionflow" | "target-native-adapter";
  verificationStatus: "verified" | "contract-defined" | "unproven";
  evidenceRequired?: boolean;
}

interface TargetCapabilityContract {
  targetCapabilityStatus: "static-only" | "source-template-required";
  sourceTemplateRequired: "true" | "false";
  actionFlowScope: string;
  nativeAdapterScope: string;
  features: TargetFeatureContract[];
}

const TARGET_CAPABILITY_CONTRACTS: Record<
  "pptx" | "xlsx",
  TargetCapabilityContract
> = {
  pptx: {
    targetCapabilityStatus: "static-only",
    sourceTemplateRequired: "false",
    actionFlowScope: "semantic-triggers-and-business-state",
    nativeAdapterScope: "presentationml-serialization-and-playback",
    features: [
      {
        featureKind: "static-slide-structure-layout",
        capabilityStatus: "supported",
        implementationResponsibility: "target-native-adapter",
        verificationStatus: "verified",
        evidenceRequired: true,
      },
      {
        featureKind: "semantic-trigger-and-business-state-orchestration",
        capabilityStatus: "supported",
        implementationResponsibility: "actionflow",
        verificationStatus: "contract-defined",
      },
      ...[
        "presentationml-slide-transition",
        "presentationml-timing",
        "click-triggered-shape-animation",
      ].map<TargetFeatureContract>((featureKind) => ({
        featureKind,
        capabilityStatus: "unsupported",
        implementationResponsibility: "target-native-adapter",
        verificationStatus: "unproven",
      })),
    ],
  },
  xlsx: {
    targetCapabilityStatus: "source-template-required",
    sourceTemplateRequired: "true",
    actionFlowScope:
      "formula-dependency-recalc-refresh-and-business-events",
    nativeAdapterScope:
      "spreadsheetml-cell-style-formula-and-package-serialization",
    features: [
      {
        featureKind:
          "source-template-cell-formula-write-and-style-preservation",
        capabilityStatus: "supported",
        implementationResponsibility: "target-native-adapter",
        verificationStatus: "verified",
        evidenceRequired: true,
      },
      {
        featureKind: "formula-dependency-recalc-orchestration",
        capabilityStatus: "supported",
        implementationResponsibility: "actionflow",
        verificationStatus: "verified",
        evidenceRequired: true,
      },
      ...[
        "source-free-workbook-generation",
        "semantic-chart-creation-or-mutation",
        "pivot-cache-or-pivot-table-mutation",
        "macro-enabled-workbook-write",
        "external-workbook-reference",
      ].map<TargetFeatureContract>((featureKind) => ({
        featureKind,
        capabilityStatus: "unsupported",
        implementationResponsibility: "target-native-adapter",
        verificationStatus: "unproven",
      })),
    ],
  },
};

export function validateDocumentOntologyContracts(
  dataset: DatasetLike,
): DocumentOntologyContractViolation[] {
  const graph = new GraphView(dataset);
  const violations: DocumentOntologyContractViolation[] = [];
  const seen = new Set<string>();
  const add = (code: string, focusNode: string, message: string) => {
    const key = `${code}\0${focusNode}`;
    if (seen.has(key)) return;
    seen.add(key);
    violations.push({ code, focusNode, message });
  };

  const capabilityProfiles = graph.instances(
    "DocumentTargetCapabilityProfile",
  );
  if (capabilityProfiles.length > 0) {
    for (const [targetKind, contract] of Object.entries(
      TARGET_CAPABILITY_CONTRACTS,
    ) as ["pptx" | "xlsx", TargetCapabilityContract][]) {
      const profiles = capabilityProfiles.filter(
        (profile) => graph.object(profile, p("targetKind")) === targetKind,
      );
      const expectedFocus = `${CDOC}${targetKind}TargetCapabilityProfile`;
      if (profiles.length !== 1) {
        add(
          "target-capability-profile-cardinality",
          expectedFocus,
          `The ${targetKind} target requires exactly one capability profile.`,
        );
        continue;
      }

      const profile = profiles[0];
      if (
        graph.object(profile, p("targetCapabilityStatus")) !==
          contract.targetCapabilityStatus ||
        graph.object(profile, p("sourceTemplateRequired")) !==
          contract.sourceTemplateRequired ||
        graph.object(profile, p("actionFlowScope")) !==
          contract.actionFlowScope ||
        graph.object(profile, p("nativeAdapterScope")) !==
          contract.nativeAdapterScope
      ) {
        add(
          "target-capability-profile-boundary",
          profile,
          `The ${targetKind} profile must preserve its declared ActionFlow, native-adapter, and source-template boundary.`,
        );
      }

      const featureByKind = new Map<string, string>();
      for (const feature of graph.objects(
        profile,
        p("hasTargetFeatureCapability"),
      )) {
        if (graph.object(feature, p("targetKind")) !== targetKind) {
          add(
            "target-capability-feature-target",
            feature,
            "A target feature capability must name the same target as its profile.",
          );
        }
        const featureKind = graph.object(feature, p("featureKind"));
        if (!featureKind) continue;
        if (featureByKind.has(featureKind)) {
          add(
            "target-capability-duplicate-feature",
            profile,
            `The ${targetKind} profile contains duplicate feature kind: ${featureKind}.`,
          );
        }
        featureByKind.set(featureKind, feature);

        const status = graph.object(feature, p("capabilityStatus"));
        const responsibility = graph.object(
          feature,
          p("implementationResponsibility"),
        );
        const verification = graph.object(feature, p("verificationStatus"));
        const evidence = graph.object(feature, p("capabilityEvidenceRef"));
        if (
          status === "supported" &&
          verification === "verified" &&
          !evidence
        ) {
          add(
            "target-capability-supported-evidence",
            feature,
            "A verified supported capability requires committed evidence.",
          );
        }
        if (
          status === "unsupported" &&
          (responsibility !== "target-native-adapter" ||
            verification !== "unproven")
        ) {
          add(
            "target-capability-unsupported-boundary",
            feature,
            "Unsupported native behavior must remain target-adapter-owned and unproven.",
          );
        }
        if (
          responsibility === "actionflow" &&
          (status !== "supported" ||
            ![
              "semantic-trigger-and-business-state-orchestration",
              "formula-dependency-recalc-orchestration",
            ].includes(featureKind))
        ) {
          add(
            "target-capability-actionflow-boundary",
            feature,
            "ActionFlow may own semantic orchestration only; it cannot claim native serialization or playback.",
          );
        }
      }

      for (const expected of contract.features) {
        const feature = featureByKind.get(expected.featureKind);
        if (!feature) {
          add(
            "target-capability-required-feature",
            profile,
            `The ${targetKind} profile is missing required feature: ${expected.featureKind}.`,
          );
          continue;
        }
        if (
          graph.object(feature, p("capabilityStatus")) !==
            expected.capabilityStatus ||
          graph.object(feature, p("implementationResponsibility")) !==
            expected.implementationResponsibility ||
          graph.object(feature, p("verificationStatus")) !==
            expected.verificationStatus ||
          (expected.evidenceRequired &&
            !graph.object(feature, p("capabilityEvidenceRef")))
        ) {
          add(
            "target-capability-feature-contract",
            feature,
            `The ${targetKind} feature ${expected.featureKind} does not match its supported/unsupported ownership and evidence contract.`,
          );
        }
      }
    }

    for (const feature of graph.instances(
      "DocumentTargetFeatureCapability",
    )) {
      const memberships = capabilityProfiles.filter((profile) =>
        graph.has(profile, p("hasTargetFeatureCapability"), feature),
      );
      if (memberships.length !== 1) {
        add(
          "target-capability-feature-membership",
          feature,
          "Every target feature capability must belong to exactly one target profile.",
        );
      }
    }
  }

  for (const template of graph.instances("GoldenDocumentTemplate")) {
    const hasApprovedRights = graph
      .objects(template, p("governedByRights"))
      .some(
        (rights) =>
          graph.has(rights, p("rightsStatus"), "approved") &&
          graph.has(rights, p("allowedUse"), "golden-template-generation"),
      );
    if (!hasApprovedRights) {
      add(
        "golden-template-rights",
        template,
        "Golden templates require approved golden-template-generation rights.",
      );
    }

    const hasPassingValidation = graph
      .objects(template, p("validationRun"))
      .some((run) => graph.has(run, p("validationStatus"), "pass"));
    if (!hasPassingValidation) {
      add(
        "golden-template-validation",
        template,
        "Golden templates require a passing validation run.",
      );
    }

    const hasAlignedPromotion = graph
      .objects(template, p("promotionDecisionRef"))
      .some(
        (decision) =>
          graph.has(decision, p("promotedItem"), template) &&
          graph.has(decision, p("promotionStatus"), "promoted") &&
          graph.has(decision, p("humanApproved"), "true"),
      );
    if (!hasAlignedPromotion) {
      add(
        "golden-template-promotion",
        template,
        "Golden template promotion must target the same template and be human approved.",
      );
    }

    const revoked = graph
      .subjects(p("revokedItem"), template)
      .some((decision) =>
        graph.hasType(decision, "DocumentRevocationDecision"),
      );
    if (revoked) {
      add(
        "golden-template-revoked",
        template,
        "A revoked template cannot remain generation authority.",
      );
    }
  }

  for (const pack of graph.instances("GoldenDocumentKnowledgePack")) {
    const hasApprovedRights = graph
      .objects(pack, p("governedByRights"))
      .some(
        (rights) =>
          graph.has(rights, p("rightsStatus"), "approved") &&
          graph.has(rights, p("allowedUse"), "ontology-derivation"),
      );
    if (!hasApprovedRights) {
      add(
        "golden-pack-rights",
        pack,
        "Golden knowledge packs require approved ontology-derivation rights.",
      );
    }

    const hasPassingValidation = graph
      .objects(pack, p("validationRun"))
      .some((run) => graph.has(run, p("validationStatus"), "pass"));
    if (!hasPassingValidation) {
      add(
        "golden-pack-validation",
        pack,
        "Golden knowledge packs require a passing validation run.",
      );
    }

    const hasAlignedPromotion = graph
      .objects(pack, p("promotionDecisionRef"))
      .some(
        (decision) =>
          graph.has(decision, p("promotedItem"), pack) &&
          graph.has(decision, p("promotionStatus"), "promoted") &&
          graph.has(decision, p("humanApproved"), "true"),
      );
    if (!hasAlignedPromotion) {
      add(
        "golden-pack-promotion",
        pack,
        "Golden knowledge-pack promotion must target the same pack and be human approved.",
      );
    }

    const revoked = graph
      .subjects(p("revokedItem"), pack)
      .some((decision) =>
        graph.hasType(decision, "DocumentRevocationDecision"),
      );
    if (revoked) {
      add(
        "golden-pack-revoked",
        pack,
        "A revoked knowledge pack cannot remain canonical authority.",
      );
    }
  }

  for (const manifest of graph.instances("DocumentTemplateManifest")) {
    const files = graph.objects(manifest, p("hasFile"));
    const entrypoint = graph.object(manifest, p("entrypointFile"));
    if (entrypoint && !files.includes(entrypoint)) {
      add(
        "manifest-entrypoint-membership",
        manifest,
        "Manifest entrypoint must be a member of its file map.",
      );
    }

    const schema = graph.object(manifest, p("inputSchemaRef"));
    const schemaFile = graph.object(schema, p("schemaFile"));
    if (schemaFile && !files.includes(schemaFile)) {
      add(
        "manifest-schema-membership",
        manifest,
        "Manifest input schema file must be a member of its file map.",
      );
    }
    const schemaHash = graph.object(schema, p("schemaHash"));
    const schemaFileHash = graph.object(schemaFile, p("fileHash"));
    if (schemaHash && schemaFileHash && schemaHash !== schemaFileHash) {
      add(
        "manifest-schema-hash",
        manifest,
        "Input schema hash must equal its manifest file hash.",
      );
    }

    const paths = new Map<string, string>();
    for (const file of files) {
      const filePath = graph.object(file, p("filePath"));
      if (!filePath) continue;
      if (paths.has(filePath) && paths.get(filePath) !== file) {
        add(
          "manifest-duplicate-path",
          manifest,
          `Manifest contains duplicate file path: ${filePath}.`,
        );
      }
      paths.set(filePath, file);
    }
  }

  for (const registry of graph.instances("DocumentTemplateRegistry")) {
    const manifests = graph.objects(registry, p("registeredManifest"));
    for (const tag of graph.objects(registry, p("registeredTag"))) {
      const resolvedManifest = graph.object(tag, p("resolvesToManifest"));
      if (resolvedManifest && !manifests.includes(resolvedManifest)) {
        add(
          "registry-tag-manifest",
          registry,
          "Registry tag must resolve to a manifest registered in the same registry.",
        );
      }
    }
  }

  for (const proposal of graph.instances(
    "DocumentModelProposal",
    "DocumentTeacherProposal",
  )) {
    const receipt = graph.object(proposal, p("modelInvocationReceipt"));
    if (graph.object(receipt, p("recordsProposal")) !== proposal) {
      add(
        "model-proposal-receipt-link",
        proposal,
        "A model proposal and its invocation receipt must point to each other.",
      );
    }

    for (const property of ["modelProvider", "modelName", "modelVersion"]) {
      const proposalValue = graph.object(proposal, p(property));
      const receiptValue = graph.object(receipt, p(property));
      if (proposalValue && receiptValue && proposalValue !== receiptValue) {
        add(
          "model-proposal-identity-lineage",
          proposal,
          "Model provider, name, and version must match the invocation receipt.",
        );
      }
    }

    const proposalOutput = graph.object(proposal, p("proposalOutputRef"));
    const receiptOutput = graph.object(receipt, p("proposalOutputRef"));
    if (proposalOutput && receiptOutput && proposalOutput !== receiptOutput) {
      add(
        "model-proposal-output-lineage",
        proposal,
        "The proposal and invocation receipt must name the same typed output candidate.",
      );
    }

    if (graph.object(receipt, p("invocationStatus")) !== "completed") {
      add(
        "model-proposal-completed-receipt",
        proposal,
        "A usable model proposal must point to a completed invocation receipt.",
      );
    }

    const promoted = graph
      .subjects(p("promotedItem"), proposal)
      .some((decision) => graph.hasType(decision, "DocumentPromotionDecision"));
    if (promoted) {
      add(
        "model-proposal-direct-promotion",
        proposal,
        "A model proposal cannot be promoted directly; deterministic admission and a governed knowledge item are required.",
      );
    }
  }

  for (const evaluation of graph.instances("DocumentArtifactEvaluation")) {
    const evaluationStatus = graph.object(evaluation, p("evaluationStatus"));
    const humanReviewStatus = graph.object(evaluation, p("humanReviewStatus"));
    const humanPreferences = graph.objects(evaluation, p("humanPreference"));
    const humanReviewReceipts = graph.objects(
      evaluation,
      p("humanReviewReceipt"),
    );
    if (
      evaluationStatus === "awaiting-human-review" &&
      (humanReviewStatus !== "pending" ||
        humanPreferences.length > 0 ||
        humanReviewReceipts.length > 0)
    ) {
      add(
        "artifact-evaluation-pending-human-review",
        evaluation,
        "An evaluation awaiting human review cannot record a human preference or completed review.",
      );
    }
    if (
      evaluationStatus === "completed" &&
      (humanReviewStatus !== "completed" ||
        humanPreferences.length !== 1 ||
        humanReviewReceipts.length !== 1)
    ) {
      add(
        "artifact-evaluation-completed-human-review",
        evaluation,
        "A completed evaluation requires exactly one human preference and one human review receipt.",
      );
    }

    if (humanReviewReceipts.length === 1) {
      const receipt = humanReviewReceipts[0];
      if (
        graph.object(receipt, p("reviewsEvaluation")) !== evaluation ||
        graph.object(receipt, p("humanPreference")) !== humanPreferences[0] ||
        !graph.has(receipt, p("humanApproved"), "true")
      ) {
        add(
          "artifact-evaluation-human-review-receipt",
          evaluation,
          "The human review receipt must target this evaluation, match its preference, and record explicit human approval.",
        );
      }
    }
  }

  for (const project of graph.instances("DocumentGenerationProject")) {
    const template = graph.object(project, p("usesTemplate"));
    const manifest = graph.object(project, p("templateManifest"));
    const registry = graph.object(project, p("templateRegistry"));
    const selection = graph.object(project, p("templateSelectionDecision"));
    const input = graph.object(project, p("semanticInputSpec"));
    const ir = graph.object(project, p("generationIR"));
    const version = graph.object(project, p("generatedDocumentVersion"));

    if (
      template &&
      graph.object(selection, p("selectedTemplate")) !== template
    ) {
      add(
        "project-selection-lineage",
        project,
        "Template selection must resolve to the template used by the project.",
      );
    }
    if (
      template &&
      graph.object(manifest, p("resolvesToTemplate")) !== template
    ) {
      add(
        "project-manifest-lineage",
        project,
        "Project manifest must resolve to the selected template.",
      );
    }
    if (
      manifest &&
      !graph.objects(registry, p("registeredManifest")).includes(manifest)
    ) {
      add(
        "project-registry-lineage",
        project,
        "Project manifest must be registered in the selected registry.",
      );
    }

    const manifestSchema = graph.object(manifest, p("inputSchemaRef"));
    const inputSchema = graph.object(input, p("inputSchemaRef"));
    if (manifestSchema && inputSchema !== manifestSchema) {
      add(
        "project-input-schema-lineage",
        project,
        "Semantic input must use the schema declared by the manifest.",
      );
    }

    if (
      template &&
      (graph.object(ir, p("usesTemplate")) !== template ||
        graph.object(ir, p("templateManifest")) !== manifest ||
        graph.object(ir, p("semanticInputSpec")) !== input)
    ) {
      add(
        "project-ir-lineage",
        project,
        "IR must share the project template, manifest, and semantic input.",
      );
    }
    const irStructureHash = graph.object(ir, p("structureHash"));
    const versionStructureHash = graph.object(version, p("structureHash"));
    if (
      irStructureHash &&
      versionStructureHash &&
      irStructureHash !== versionStructureHash
    ) {
      add(
        "project-structure-lineage",
        project,
        "IR and generated canonical version must share one structure hash.",
      );
    }
    if (
      !graph
        .objects(project, p("generationValidation"))
        .some((run) => graph.has(run, p("validationStatus"), "pass"))
    ) {
      add(
        "project-generation-validation",
        project,
        "Template generation requires a passing validation run.",
      );
    }

    const templateSlots = new Set(
      graph.objects(template, p("hasTemplateSlot")),
    );
    const slotCounts = new Map<string, number>();
    for (const binding of graph.objects(project, p("hasSlotBinding"))) {
      const slot = graph.object(binding, p("slotRef"));
      if (!slot) continue;
      slotCounts.set(slot, (slotCounts.get(slot) ?? 0) + 1);
      if (!templateSlots.has(slot)) {
        add(
          "project-foreign-slot",
          project,
          "Slot bindings may only reference slots owned by the selected template.",
        );
      }
    }
    for (const slot of templateSlots) {
      const count = slotCounts.get(slot) ?? 0;
      if (graph.has(slot, p("requiredSlot"), "true") && count !== 1) {
        add(
          "project-required-slot",
          project,
          "Every required template slot must be bound exactly once.",
        );
      } else if (count > 1) {
        add(
          "project-duplicate-slot",
          project,
          "A template slot cannot have duplicate binding records.",
        );
      }
    }

    const receipts = graph.objects(project, p("renderReceipt"));
    for (const target of graph.objects(
      project,
      p("requiredRenderTargetKind"),
    )) {
      if (!graph.has(template, p("supportedRenderTargetKind"), target)) {
        add(
          "project-unsupported-target",
          project,
          `Template does not support required render target: ${target}.`,
        );
      }
      const covered = receipts.some(
        (receipt) =>
          graph.hasType(receipt, "PassingDocumentRenderReceipt") &&
          graph.has(receipt, p("targetKind"), target),
      );
      if (!covered) {
        add(
          "project-target-coverage",
          project,
          `Required render target lacks a passing receipt: ${target}.`,
        );
      }
    }
  }

  for (const receipt of graph.instances(
    "DocumentRenderReceipt",
    "PassingDocumentRenderReceipt",
  )) {
    const project = graph.object(receipt, p("generationProject"));
    const template = graph.object(receipt, p("usesTemplate"));
    const manifest = graph.object(receipt, p("templateManifest"));
    const input = graph.object(receipt, p("semanticInputSpec"));
    const ir = graph.object(receipt, p("generationIR"));
    const compiler = graph.object(receipt, p("compilerProfile"));
    const target = graph.object(receipt, p("targetKind"));

    if (!graph.objects(project, p("renderReceipt")).includes(receipt)) {
      add(
        "receipt-project-link",
        receipt,
        "Render receipt must be listed by its generation project.",
      );
    }
    if (
      project &&
      (graph.object(project, p("usesTemplate")) !== template ||
        graph.object(project, p("templateManifest")) !== manifest ||
        graph.object(project, p("semanticInputSpec")) !== input ||
        graph.object(project, p("generationIR")) !== ir)
    ) {
      add(
        "receipt-project-lineage",
        receipt,
        "Render receipt must share its project's complete generation lineage.",
      );
    }
    if (
      graph.object(manifest, p("manifestHash")) !==
      graph.object(receipt, p("manifestHash"))
    ) {
      add(
        "receipt-manifest-hash",
        receipt,
        "Receipt manifest hash must equal its linked manifest hash.",
      );
    }
    if (
      graph.object(input, p("inputDataHash")) !==
      graph.object(receipt, p("inputDataHash"))
    ) {
      add(
        "receipt-input-hash",
        receipt,
        "Receipt input hash must equal its linked semantic input hash.",
      );
    }
    if (target && graph.object(compiler, p("targetKind")) !== target) {
      add(
        "receipt-compiler-target",
        receipt,
        "Receipt target must equal its compiler profile target.",
      );
    }
    if (
      graph.hasType(receipt, "PassingDocumentRenderReceipt") &&
      (!graph.hasType(compiler, "DeterministicDocumentCompilerProfile") ||
        !graph.has(compiler, p("compilerDeterministic"), "true") ||
        !graph.has(compiler, p("finalWriterAllowed"), "true") ||
        !graph.has(compiler, p("canonicalAuthority"), "false"))
    ) {
      add(
        "receipt-deterministic-final-writer",
        receipt,
        "A passing render receipt must use the deterministic compiler as final writer without granting it semantic authority.",
      );
    }
    if (
      target &&
      !graph.has(template, p("supportedRenderTargetKind"), target)
    ) {
      add(
        "receipt-template-target",
        receipt,
        "Receipt target must be supported by its template.",
      );
    }
    const hasBlockingWarning = graph
      .subjects(p("warningReceipt"), receipt)
      .some(
        (warning) =>
          graph.hasType(warning, "DocumentGenerationWarning") &&
          graph.has(warning, p("warningSeverity"), "blocking"),
      );
    if (
      graph.hasType(receipt, "PassingDocumentRenderReceipt") &&
      hasBlockingWarning
    ) {
      add(
        "receipt-blocking-warning",
        receipt,
        "A passing receipt cannot have a linked blocking warning.",
      );
    }
  }

  for (const receipt of graph.instances(
    "PassingConnectedDocumentPublicationReceipt",
    "GoogleDocsPublicationReceipt",
    "NotionPublicationReceipt",
  )) {
    const provider = graph.object(receipt, p("provider"));
    const sourceVersion = graph.object(receipt, p("sourceVersionRef"));
    const approvedVersion = graph.object(receipt, p("approvedVersionRef"));
    const targetVersion = graph.object(receipt, p("targetVersionRef"));
    const expectedStructure = graph.object(receipt, p("expectedStructureHash"));
    const observedStructure = graph.object(receipt, p("observedStructureHash"));
    const canonicalVersion = graph.object(
      receipt,
      p("canonicalDocumentVersion"),
    );
    const canonicalStructure = graph.object(
      canonicalVersion,
      p("structureHash"),
    );
    const canonicalContent = graph.object(canonicalVersion, p("contentHash"));
    const approval = graph.object(receipt, p("approvalRef"));
    const readback = graph.object(receipt, p("verifiedByReadback"));

    if (sourceVersion && approvedVersion && sourceVersion !== approvedVersion) {
      add(
        "publication-approved-version",
        receipt,
        "Publication source and approved version must match.",
      );
    }
    if (
      sourceVersion &&
      graph.object(canonicalVersion, p("versionRef")) !== sourceVersion
    ) {
      add(
        "publication-canonical-version",
        receipt,
        "Publication version must match the linked canonical document version.",
      );
    }
    if (
      approvedVersion &&
      graph.object(approval, p("approvedVersionRef")) !== approvedVersion
    ) {
      add(
        "publication-approval-lineage",
        receipt,
        "Publication approval must name the same canonical version.",
      );
    }
    if (
      expectedStructure &&
      observedStructure &&
      expectedStructure !== observedStructure
    ) {
      add(
        "publication-structure-hash",
        receipt,
        "Expected and observed publication structure hashes must match.",
      );
    }
    if (
      expectedStructure &&
      canonicalStructure &&
      expectedStructure !== canonicalStructure
    ) {
      add(
        "publication-canonical-structure-hash",
        receipt,
        "Publication structure hash must equal the canonical document structure hash.",
      );
    }
    if (
      provider &&
      (graph.object(readback, p("provider")) !== provider ||
        graph.object(readback, p("targetVersionRef")) !== targetVersion ||
        graph.object(readback, p("observedStructureHash")) !==
          observedStructure)
    ) {
      add(
        "publication-readback-lineage",
        receipt,
        "Publication provider, provider version, and observed structure must match readback.",
      );
    }

    if (graph.hasType(receipt, "GoogleDocsPublicationReceipt")) {
      const expectedContent = graph.object(receipt, p("expectedContentHash"));
      const observedContent = graph.object(receipt, p("observedContentHash"));
      if (
        !expectedContent ||
        expectedContent !== observedContent ||
        graph.object(readback, p("observedContentHash")) !== observedContent
      ) {
        add(
          "google-docs-content-readback",
          receipt,
          "Google Docs expected, observed, and readback content hashes must match.",
        );
      }
      if (
        expectedContent &&
        canonicalContent &&
        expectedContent !== canonicalContent
      ) {
        add(
          "google-docs-canonical-content-hash",
          receipt,
          "Google Docs content hash must equal the canonical document content hash.",
        );
      }
    }
  }

  return violations;
}
